import { useCallback, useMemo } from 'react'

import { useUmbrella } from '@modules-deprecated/app/umbrellas'

import { useIsAdvisorExpertLayer } from '../../AdvisorLayer/hooks/useIsAdvisorExpertLayer'
import { navigationMenuConfig } from '../constants/navigationMenuConfig'
import { navigationMenuExpertConfig } from '../constants/navigationMenuExpertConfig'
import { navigationMenuUmbrellaConfig } from '../constants/navigationMenuUmbrellaConfig'
import { NavigationMenuItem, NavigationMenuSubItem } from '../types/NavigationMenuItem'
import { hasRequiredScopes } from '../utils/hasRequiredScopes'
import { hasSubItems } from '../utils/hasSubItems'
import { isNavigationMenuItemVisible } from '../utils/isNavigationMenuItemVisible'
import { removeInvalidMenuItems } from '../utils/removeInvalidMenuItems'
import { useNavigationMenuVisibilityConditions } from './useNavigationMenuVisibilityConditions'

export const useNavigationMenuFilters = () => {
  const { umbrella } = useUmbrella()
  const { navigationMenuVisibilityConditions } = useNavigationMenuVisibilityConditions()
  const isExpertLayer = useIsAdvisorExpertLayer()

  const organizationConfig = isExpertLayer ? navigationMenuExpertConfig : navigationMenuConfig
  const config = umbrella ? navigationMenuUmbrellaConfig : organizationConfig

  const filterVisibleSubItems = useCallback(
    (subItems: NavigationMenuSubItem[]): NavigationMenuSubItem[] =>
      subItems.filter(
        ({ id, scopes }) =>
          isNavigationMenuItemVisible(id, navigationMenuVisibilityConditions) && hasRequiredScopes(scopes),
      ),
    [navigationMenuVisibilityConditions],
  )

  const filterVisibleItems = useCallback(
    (items: NavigationMenuItem[]) => {
      return (
        items
          .filter(
            ({ id, scopes }) =>
              isNavigationMenuItemVisible(id, navigationMenuVisibilityConditions) && hasRequiredScopes(scopes),
          )
          .map((item) => (hasSubItems(item) ? { ...item, subItems: filterVisibleSubItems(item.subItems) } : item))
          // Don't show the parent menu item if subItems is an empty array (e.g., due to business rules or missing scopes)
          .filter((item) => item?.subItems?.length !== 0)
      )
    },
    [filterVisibleSubItems, navigationMenuVisibilityConditions],
  )

  const filteredMenuItems = useMemo(() => {
    const configWithoutInvalidMenuItems = removeInvalidMenuItems(config)

    return filterVisibleItems(configWithoutInvalidMenuItems)
  }, [config, filterVisibleItems])

  return { filteredMenuItems }
}
